<script setup>
/**
 * What the customer will see, drawn the way WhatsApp draws it.
 *
 * It does not only LOOK like WhatsApp, it COUNTS like WhatsApp: three options or fewer are
 * reply buttons (20 characters each), four or more are a list behind one button (24 each),
 * and anything over is drawn in ruby with its count. Chatwoot checks none of these limits,
 * so this screen is the only place an owner finds out before a customer does.
 *
 * The language toggle is the PREVIEW's, not the dashboard's: an owner working in English
 * still has to check the Arabic menu, right-to-left, before a customer sees it. A title
 * missing in one language is drawn from the other — exactly the fallback the assistant sends —
 * and the page says so.
 *
 * Colour only through `n-*` tokens (waslo-lint R1-R4); direction only through logical
 * utilities (R6) and a `dir` on the phone screen, so the whole mock mirrors in Arabic.
 */
import { computed, ref } from 'vue';

import { useWasloI18n, wasloT } from '../i18n.js';
import { previewModel } from '../helpers/menu-draft.js';

const props = defineProps({
  /** The draft, in the shape `helpers/menu-draft.js` builds. */
  draft: { type: Object, required: true },
  /** The language to open on; the toggle takes over from there. */
  initialLang: {
    type: String,
    default: 'en',
    validator: value => ['en', 'ar'].includes(value),
  },
});

const { t } = useWasloI18n();

const lang = ref(props.initialLang);
const model = computed(() => previewModel(props.draft, lang.value));

// Customer-facing copy, so it is read in the PREVIEW's language, not the dashboard's.
const sampleBody = computed(() =>
  wasloT(null, 'WASLO.MENUS.EDITOR.PREVIEW.SAMPLE_BODY', lang.value)
);

const overCount = computed(
  () =>
    model.value.rows.filter(row => row.over).length +
    (model.value.buttonOver ? 1 : 0) +
    (model.value.bodyOver ? 1 : 0)
);

const hasBorrowed = computed(() => model.value.rows.some(row => row.borrowed));

const overLabel = row =>
  t('WASLO.MENUS.EDITOR.PREVIEW.OVER', {
    count: row.length,
    limit: model.value.limit,
  });

const toggleClass = active =>
  active
    ? 'bg-n-solid-3 text-n-slate-12'
    : 'text-n-slate-11 hover:bg-n-alpha-2';
</script>

<template>
  <section
    class="flex flex-col gap-3"
    data-waslo-menu-preview
    :data-kind="model.kind"
  >
    <div
      class="flex items-center self-start gap-1 p-1 rounded-lg outline outline-1 outline-n-weak"
      role="group"
      :aria-label="t('WASLO.MENUS.EDITOR.PREVIEW.LANGUAGE')"
    >
      <button
        type="button"
        class="px-3 py-1 text-sm rounded-md"
        :class="toggleClass(lang === 'en')"
        :aria-pressed="lang === 'en'"
        data-waslo-menu-preview-en
        @click="lang = 'en'"
      >
        {{ t('WASLO.MENUS.EDITOR.PREVIEW.ENGLISH') }}
      </button>
      <button
        type="button"
        class="px-3 py-1 text-sm rounded-md"
        :class="toggleClass(lang === 'ar')"
        :aria-pressed="lang === 'ar'"
        data-waslo-menu-preview-ar
        @click="lang = 'ar'"
      >
        {{ t('WASLO.MENUS.EDITOR.PREVIEW.ARABIC') }}
      </button>
    </div>

    <!-- The phone screen. `dir` here, so the bubble, the buttons and the sheet all mirror. -->
    <div
      :dir="model.dir"
      class="flex flex-col gap-2 p-4 rounded-2xl bg-n-alpha-1"
      data-waslo-menu-preview-screen
    >
      <div
        class="flex flex-col self-start w-full overflow-hidden max-w-[20rem] rounded-2xl outline outline-1 outline-n-weak bg-n-solid-1"
      >
        <p
          dir="auto"
          class="px-3 py-2 mb-0 text-sm leading-6 whitespace-pre-wrap"
          :class="model.body ? 'text-n-slate-12' : 'text-n-slate-10'"
          data-waslo-menu-preview-body
        >
          {{ model.body || sampleBody }}
        </p>
        <div
          v-if="model.kind === 'list'"
          class="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border-t border-n-weak"
          :class="model.buttonOver ? 'text-n-ruby-9' : 'text-n-blue-11'"
          data-waslo-menu-preview-button
        >
          <span class="size-4 i-lucide-list" aria-hidden="true" />
          <bdi>{{ model.button }}</bdi>
        </div>
      </div>

      <!-- Up to three: reply buttons under the bubble. -->
      <div
        v-if="model.kind === 'buttons'"
        class="flex flex-col self-start w-full gap-1 max-w-[20rem]"
      >
        <div
          v-for="(row, index) in model.rows"
          :key="index"
          class="flex flex-col items-center px-3 py-2 text-sm font-medium text-center rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
          :class="row.over ? 'text-n-ruby-9' : 'text-n-blue-11'"
          data-waslo-menu-preview-row
          :data-over="row.over"
        >
          <bdi>{{ row.title || '—' }}</bdi>
          <span v-if="row.over" class="text-xs">{{ overLabel(row) }}</span>
        </div>
      </div>

      <!-- Four or more: the sheet WhatsApp opens from the list button. -->
      <div
        v-else
        class="flex flex-col self-start w-full mt-2 max-w-[20rem] rounded-2xl outline outline-1 outline-n-weak bg-n-solid-2"
        data-waslo-menu-preview-sheet
      >
        <p
          class="px-4 pt-3 pb-2 mb-0 text-sm font-medium text-center text-n-slate-12"
        >
          <bdi>{{ model.button }}</bdi>
        </p>
        <ul class="flex flex-col mb-0 divide-y divide-n-weak">
          <li
            v-for="(row, index) in model.rows"
            :key="index"
            class="flex items-center justify-between gap-3 px-4 py-2.5"
            :class="row.over ? 'text-n-ruby-9' : 'text-n-slate-12'"
            data-waslo-menu-preview-row
            :data-over="row.over"
          >
            <span class="flex flex-col min-w-0">
              <bdi class="text-sm">{{ row.title || '—' }}</bdi>
              <span v-if="row.over" class="text-xs">{{ overLabel(row) }}</span>
            </span>
            <span
              class="rounded-full shrink-0 size-4 outline outline-1 outline-n-slate-7"
              aria-hidden="true"
            />
          </li>
        </ul>
      </div>
    </div>

    <p
      v-if="overCount"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-menu-preview-over
    >
      {{ t('WASLO.MENUS.EDITOR.PREVIEW.TOO_LONG', { count: overCount }) }}
    </p>
    <p
      v-if="hasBorrowed"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-menu-preview-borrowed
    >
      {{ t('WASLO.MENUS.EDITOR.PREVIEW.BORROWED') }}
    </p>
  </section>
</template>
